import { fetchActivity } from "@/api/repos";
import type { ActivityCell, Repo } from "@/types/repo";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "../hooks/useTranslation";

interface Props {
  repos: Repo[];
}

type Mode = "combined" | number; // number = repo_id

const WEEKS = 52;
const DAYS = 7;
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function buildGrid(cells: ActivityCell[]): number[][] {
  const grid: number[][] = Array.from({ length: WEEKS }, () =>
    Array(DAYS).fill(0),
  );
  for (const c of cells) {
    const w = Math.min(c.week - 1, WEEKS - 1);
    const d = Math.min(c.day_of_week, DAYS - 1);
    grid[w][d] = c.count;
  }
  return grid;
}

function colorForCount(count: number, max: number): string {
  if (max === 0 || count === 0) return "var(--color-bg-surface)";
  const pct = count / max;
  if (pct < 0.25) return "rgba(29,79,255,0.25)";
  if (pct < 0.5) return "rgba(29,79,255,0.5)";
  if (pct < 0.75) return "rgba(29,79,255,0.7)";
  return "var(--color-primary)";
}

export function ContributionGraph({ repos }: Props) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>("combined");
  const [cells, setCells] = useState<ActivityCell[]>([]);
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    const params = mode === "combined" ? {} : { repo_id: mode as number };
    fetchActivity(params)
      .then(setCells)
      .finally(() => setLoading(false));
  }, [mode]);

  const grid = buildGrid(cells);
  const max = Math.max(...cells.map((c) => c.count), 1);

  return (
    <div>
      {/* Mode selector */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setMode("combined")}
          className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
            mode === "combined"
              ? "border-primary bg-primary-muted text-primary"
              : "border-border text-text-muted hover:border-primary"
          }`}
        >
          All repos
        </button>
        {repos.map((r) => (
          <button
            key={r.id}
            onClick={() => setMode(r.id)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
              mode === r.id
                ? "border-primary bg-primary-muted text-primary"
                : "border-border text-text-muted hover:border-primary"
            }`}
          >
            {r.name}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div ref={containerRef} className="relative overflow-x-auto">
        {loading ? (
          <div className="h-28 flex items-center justify-center text-sm text-text-muted">
            {t("common.loading")}
          </div>
        ) : (
          <svg
            viewBox={`0 0 ${WEEKS * 14} ${DAYS * 14}`}
            className="w-full max-w-[900px]"
            aria-label="Contribution heatmap"
          >
            {/* Day labels */}
            {DAY_LABELS.map((label, d) =>
              d % 2 === 1 ? (
                <text
                  key={d}
                  x={-4}
                  y={d * 14 + 11}
                  textAnchor="end"
                  fontSize={9}
                  fill="var(--color-text-muted)"
                >
                  {label}
                </text>
              ) : null,
            )}

            {grid.map((week, w) =>
              week.map((count, d) => (
                <rect
                  key={`${w}-${d}`}
                  x={w * 14}
                  y={d * 14}
                  width={12}
                  height={12}
                  rx={2}
                  fill={colorForCount(count, max)}
                  style={{ cursor: count > 0 ? "pointer" : "default" }}
                  onMouseEnter={(e) => {
                    if (!containerRef.current) return;
                    const rect = containerRef.current.getBoundingClientRect();
                    setTooltip({
                      text: `Week ${w + 1}, ${DAY_LABELS[d]}: ${count} commits`,
                      x: e.clientX - rect.left,
                      y: e.clientY - rect.top - 36,
                    });
                  }}
                  onMouseLeave={() => setTooltip(null)}
                />
              )),
            )}
          </svg>
        )}

        {tooltip && (
          <div
            className="absolute z-10 px-2.5 py-1.5 rounded-lg text-xs bg-bg-card border border-border shadow-lg text-text-primary pointer-events-none whitespace-nowrap"
            style={{ left: tooltip.x, top: tooltip.y }}
          >
            {tooltip.text}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-2 mt-3 text-xs text-text-muted">
        <span>Less</span>
        {[
          "var(--color-bg-surface)",
          "rgba(29,79,255,0.25)",
          "rgba(29,79,255,0.5)",
          "rgba(29,79,255,0.7)",
          "var(--color-primary)",
        ].map((c, i) => (
          <span
            key={i}
            className="w-3 h-3 rounded-sm inline-block"
            style={{ background: c }}
          />
        ))}
        <span>More</span>
      </div>
    </div>
  );
}
